// ============================================================================
// checklistViewAdapter — project the new book-model docs into the legacy
// ChecklistItem shape that TimelineContext + downstream UI still consume.
// ----------------------------------------------------------------------------
// Bridges the type gap during the Book-model cutover. Once every consumer
// speaks the new (definition + page + layout) shapes natively, this file
// can be deleted.
//
// Projection rules:
//   ChecklistDefinition           →  most of the ChecklistItem body
//   ChecklistCompletionState      →  workflow/completion/proof/timer fields
//   LayoutVersion.nodes[id]       →  position, wire visibility
//   ChecklistType renaming        →  INDIVIDUAL is exposed as legacy 'MAIN'
//                                    for the union `'MAIN' | 'DAILY' |
//                                    'WEEKLY'` used across the UI.
// ============================================================================

import type { ChecklistItem, ChecklistWorkflowStatus } from '../types/schema'
import type {
  ChecklistBookPage,
  ChecklistCompletionState,
  ChecklistDefinition,
  ChecklistDefinitionVersionPatch,
  ChecklistDetailVersion,
  ChecklistLayoutVersion,
  ChecklistType,
  PageKey,
} from '../types/checklistBooks'
import { CHECKLIST_CANVAS_LAYOUT_VERSION } from '../types/checklistCanvas'
import { applyChecklistDetailPatch } from './checklistDetailVersionService'
import { deriveRecurringScheduleForOccurrence } from '../utils/recurringScheduleDerivation'
import { deriveChecklistTimeStatus } from '../utils/checklistTimeStatus'
import { getCachedTodosLeadDays } from './workspaceTodosLeadDaysCache'
import { workflowStatusToChecklistBoardStatus } from './checklistStatusMachine'
import { effectivePeriodBoardStatus } from './repeat/completions'

/** Identity now that the legacy union has been renamed to
 *  `'INDIVIDUAL' | 'DAILY' | 'WEEKLY'`. Kept as an exported function so
 *  callers don't need to change; and to future-proof if a divergence ever
 *  re-emerges. */
export const projectLegacyChecklistTypeToNew = (
  legacy: ChecklistItem['checklistType'] | null | undefined,
): ChecklistType => {
  if (legacy === 'DAILY') return 'DAILY'
  if (legacy === 'WEEKLY') return 'WEEKLY'
  return 'INDIVIDUAL'
}

// ---------------------------------------------------------------------------
// Projection
// ---------------------------------------------------------------------------

export const projectDefinitionToChecklistItem = (
  definition: ChecklistDefinition,
  opts: {
    completion?: ChecklistCompletionState
    layoutNode?: ChecklistLayoutVersion['nodes'][string]
    detailVersion?: ChecklistDetailVersion | null
    pageDetailPatch?: ChecklistDefinitionVersionPatch | null
    /** Occurrence key of the page this projection is anchored at — surfaced
     *  on the legacy `occurrences` map so consumers reading the singleton's
     *  per-occurrence field keep working. */
    pageKey?: PageKey
  } = {},
): ChecklistItem => {
  const {
    completion,
    layoutNode,
    detailVersion,
    pageDetailPatch,
    pageKey,
  } = opts
  const effectiveDefinition = applyChecklistDetailPatch(
    definition,
    detailVersion?.checklistPatches?.[definition.id] ?? null,
    pageDetailPatch ?? null,
  )
  const legacyType = effectiveDefinition.checklistType

  // Prefer the layout-version node's position (canonical source), then fall
  // back to any `position` written on the definition doc (batch.update in
  // ChecklistDrawingBoard mirrors position there so the value survives if the
  // fire-and-forget layout write ever loses its race), then to origin.
  const definitionPositionRaw = (effectiveDefinition as { position?: unknown }).position
  const definitionPosition = definitionPositionRaw
    && typeof definitionPositionRaw === 'object'
    && typeof (definitionPositionRaw as { x?: unknown }).x === 'number'
    && typeof (definitionPositionRaw as { y?: unknown }).y === 'number'
    ? (definitionPositionRaw as { x: number; y: number })
    : null
  const position = layoutNode?.position ?? definitionPosition ?? { x: 0, y: 0 }

  const isNotRequiredForPage = Boolean(
    pageKey
    && (legacyType === 'DAILY' || legacyType === 'WEEKLY')
    && !completion?.boardStatus
    && effectivePeriodBoardStatus(completion, effectiveDefinition, pageKey) === 'COMPLETE',
  )

  const occurrences: ChecklistItem['occurrences'] =
    pageKey && completion
      ? {
          [pageKey]: {
            isCompleted: Boolean(completion.isCompleted),
            workflowStatus: completion.workflowStatus ?? null,
            completedAt: completion.completedAt ?? null,
            completedById: completion.completedById ?? null,
            completedByName: completion.completedByName ?? null,
          },
        }
      : pageKey && isNotRequiredForPage
        ? {
            [pageKey]: {
              isCompleted: false,
              workflowStatus: 'NOT_REQUIRED',
              boardStatus: 'COMPLETE',
              completedAt: null,
              completedById: null,
              completedByName: null,
            },
          }
        : (legacyType === 'DAILY' || legacyType === 'WEEKLY')
          ? {}
          : undefined

  const projectedWorkflow: ChecklistWorkflowStatus =
    completion?.workflowStatus ??
    (isNotRequiredForPage ? 'NOT_REQUIRED' : (effectiveDefinition.assigneeId ? 'ASSIGNED' : 'CREATED'))

  return {
    // --- Identity / structure (from definition) ---
    id: definition.id,
    workspaceId: effectiveDefinition.workspaceId,
    workspaceSlug: effectiveDefinition.workspaceSlug,
    parentContainerId: effectiveDefinition.parentContainerId,
    board: effectiveDefinition.board,
    publicId: effectiveDefinition.publicId,
    routePath: effectiveDefinition.routePath,
    parentRoutePath: effectiveDefinition.parentRoutePath,
    deletedFromBoard: effectiveDefinition.deletedFromBoard ?? null,
    deletedAt: effectiveDefinition.deletedAt,

    // --- Content ---
    title: effectiveDefinition.title,
    description: effectiveDefinition.description,
    completionCriteria: effectiveDefinition.completionCriteria,
    priority: effectiveDefinition.priority,
    weightage: effectiveDefinition.weightage,
    isDescriptionPrivate: effectiveDefinition.isDescriptionPrivate,

    // --- Board / completion state (from page.completions) ---
    boardStatus: completion?.boardStatus ?? (projectedWorkflow ? workflowStatusToChecklistBoardStatus(projectedWorkflow) : null),
    isCompleted: typeof completion?.isCompleted === 'boolean'
      ? completion.isCompleted
      : projectedWorkflow === 'COMPLETED',
    workflowStatus: projectedWorkflow,
    previousWorkflowStatus: completion?.previousWorkflowStatus ?? null,
    isReady: completion?.isReady ?? projectedWorkflow !== 'COMPLETED',
    resourceReadySku: completion?.resourceReadySku ?? null,
    resourceReadyAt: completion?.resourceReadyAt ?? null,

    // --- Position (from layout node) ---
    position: {
      x: Number.isFinite(position.x) ? position.x : 0,
      y: Number.isFinite(position.y) ? position.y : 0,
    },
    workspaceCanvasLayoutVersion: CHECKLIST_CANVAS_LAYOUT_VERSION,

    // --- Dependencies / gates (from definition) ---
    prerequisites: effectiveDefinition.prerequisites ?? [],
    dependencyNodeIds: effectiveDefinition.dependencyNodeIds ?? [],
    invalidDependencyNodeIds: effectiveDefinition.invalidDependencyNodeIds ?? [],
    blockedByExternal: effectiveDefinition.blockedByExternal ?? null,
    blockedByExternalId: effectiveDefinition.blockedByExternalId ?? null,
    interWorkspaceTargetId: effectiveDefinition.interWorkspaceTargetId ?? null,
    dependencyLogicGate: effectiveDefinition.dependencyLogicGate ?? undefined,
    blockedByNodeIds: effectiveDefinition.blockedByNodeIds ?? [],
    isBlockerForNodeIds: effectiveDefinition.isBlockerForNodeIds ?? [],
    nodeKind: effectiveDefinition.nodeKind ?? undefined,
    gateInputIds: effectiveDefinition.gateInputIds ?? [],
    hasAutoSpawnTrigger: effectiveDefinition.hasAutoSpawnTrigger ?? false,
    autoSpawnTemplates: effectiveDefinition.autoSpawnTemplates ?? [],

    // --- Proof/verification RULES (from definition) ---
    requiresProofFile: effectiveDefinition.requiresProofFile ?? false,
    requiresCoSign: effectiveDefinition.requiresCoSign ?? false,
    coSignerRoleTarget: effectiveDefinition.coSignerRoleTarget ?? undefined,
    proofApproverId: effectiveDefinition.proofApproverId ?? null,
    proofApproverName: effectiveDefinition.proofApproverName ?? null,
    requiresLocationPin: effectiveDefinition.requiresLocationPin ?? false,
    targetCoordinates: effectiveDefinition.targetCoordinates ?? null,

    // --- Proof/verification SUBMISSION (from page.completions) ---
    proofFileUrl: completion?.proofFileUrl ?? null,
    proofFileName: completion?.proofFileName ?? null,
    proofFileStoragePath: completion?.proofFileStoragePath ?? null,
    proofSubmissionId: completion?.proofSubmissionId ?? null,
    proofUploadedById: completion?.proofUploadedById ?? null,
    proofUploadedByName: completion?.proofUploadedByName ?? null,
    proofUploadedAt: completion?.proofUploadedAt ?? null,
    coSignedByUid: completion?.coSignedByUid ?? null,
    coSignedByName: completion?.coSignedByName ?? null,
    isPendingReview: completion?.isPendingReview ?? false,
    proofReviewStatus: completion?.proofReviewStatus ?? null,
    proofReviewedAt: completion?.proofReviewedAt ?? null,
    proofReviewedById: completion?.proofReviewedById ?? null,
    proofReviewedByName: completion?.proofReviewedByName ?? null,
    proofRejectionNote: completion?.proofRejectionNote ?? null,
    verifiedCoordinates: completion?.verifiedCoordinates ?? null,
    proofVideoUrl: completion?.proofVideoUrl ?? null,
    memoStoragePath: completion?.memoStoragePath ?? null,
    memoFileName: completion?.memoFileName ?? null,
    memoContentType: completion?.memoContentType ?? null,
    memoUploadedById: completion?.memoUploadedById ?? null,
    memoUploadedByName: completion?.memoUploadedByName ?? null,
    memoUploadedAt: completion?.memoUploadedAt ?? null,
    voiceMemoBlobUrl: completion?.voiceMemoBlobUrl ?? null,

    // --- Timer / crisis (from page.completions) ---
    countdownHours: effectiveDefinition.countdownHours ?? null,
    timerStartedAt: completion?.timerStartedAt ?? null,
    timerExpiredAt: completion?.timerExpiredAt ?? null,
    isUrgentCrisisMode: completion?.isUrgentCrisisMode ?? false,

    // --- Delay policy (from definition) ---
    enforcePaintSpringDelay: effectiveDefinition.enforcePaintSpringDelay ?? false,
    delayDurationHours: effectiveDefinition.delayDurationHours ?? undefined,

    // --- Aggregates ---
    commentsCount: completion?.commentsCount ?? 0,
    actualCost: completion?.actualCost ?? null,

    // --- Risk & resource matrix (from definition) ---
    estimatedHours: effectiveDefinition.estimatedHours ?? null,
    complexityStars: effectiveDefinition.complexityStars ?? undefined,
    urgencyStars: effectiveDefinition.urgencyStars ?? undefined,
    importanceStars: effectiveDefinition.importanceStars ?? undefined,
    currencyCode: effectiveDefinition.currencyCode ?? undefined,

    // --- Scheduling / calendar (from definition; recurring items DERIVE
    //     the schedule from the focused occurrence pageKey so calendar,
    //     dashboard, dependency, and drawer UIs all see per-instance
    //     dates without a separate write. DAILY → DATED with start=end
    //     equal to the day; WEEKLY → DURATION spanning Mon→Sun.) ---
    ...(() => {
      const isRecurring = legacyType === 'DAILY' || legacyType === 'WEEKLY'
      const scheduleFields = (() => {
        if (isRecurring && pageKey) {
          const derived = deriveRecurringScheduleForOccurrence(
            legacyType === 'WEEKLY' ? 'WEEKLY' : 'DAILY',
            pageKey,
          )
          return {
            scheduleMode: derived.scheduleMode,
            scheduleStartDate: derived.scheduleStartDate,
            scheduleEndDate: derived.scheduleEndDate,
            searchDateBuckets: effectiveDefinition.searchDateBuckets,
            hasTimeWindow: effectiveDefinition.hasTimeWindow,
            scheduleTimeFrom: effectiveDefinition.scheduleTimeFrom,
            scheduleTimeTo: effectiveDefinition.scheduleTimeTo,
            startDate: derived.scheduleStartDate,
            endDate: derived.scheduleEndDate,
          }
        }
        return {
          scheduleMode: effectiveDefinition.scheduleMode,
          scheduleStartDate: effectiveDefinition.scheduleStartDate,
          scheduleEndDate: effectiveDefinition.scheduleEndDate,
          searchDateBuckets: effectiveDefinition.searchDateBuckets,
          hasTimeWindow: effectiveDefinition.hasTimeWindow,
          scheduleTimeFrom: effectiveDefinition.scheduleTimeFrom,
          scheduleTimeTo: effectiveDefinition.scheduleTimeTo,
          startDate: effectiveDefinition.startDate ?? null,
          endDate: effectiveDefinition.endDate ?? null,
        }
      })()
      // Derived, non-persisted lifecycle chip. Reads workspace lead-days
      // from a synchronous cache primed by settings subscribers.
      const timeStatus = deriveChecklistTimeStatus({
        scheduleMode: scheduleFields.scheduleMode ?? null,
        scheduleStartDate: scheduleFields.scheduleStartDate ?? null,
        scheduleEndDate: scheduleFields.scheduleEndDate ?? null,
        startDate: scheduleFields.startDate ?? null,
        endDate: scheduleFields.endDate ?? null,
        leadDays: getCachedTodosLeadDays(effectiveDefinition.workspaceId),
      })
      return {
        ...scheduleFields,
        timeStatus,
      }
    })(),

    // --- Assignment (from definition) ---
    assigneeId: effectiveDefinition.assigneeId,
    assigneeName: effectiveDefinition.assigneeName,
    assigneeHistoryIds: effectiveDefinition.assigneeHistoryIds,

    // --- Attachments (from definition) ---
    attachedFormIds: effectiveDefinition.attachedFormIds,
    attachedDesignIds: effectiveDefinition.attachedDesignIds,
    attachedUploadIds: effectiveDefinition.attachedUploadIds,
    attachedLinkIds: effectiveDefinition.attachedLinkIds,
    cardBackgroundFileId: effectiveDefinition.cardBackgroundFileId,
    cardBackgroundUrl: effectiveDefinition.cardBackgroundUrl,
    cardBackgroundFileName: effectiveDefinition.cardBackgroundFileName,
    cardBackgroundStoragePath: effectiveDefinition.cardBackgroundStoragePath,

    // --- Access (from definition) ---
    helperVisibilityMask: effectiveDefinition.helperVisibilityMask,
    isHelperVisible: effectiveDefinition.isHelperVisible,
    scopeRead: Array.isArray(effectiveDefinition.scopeRead)
      ? effectiveDefinition.scopeRead.filter((userId): userId is string => typeof userId === 'string')
      : undefined,
    scopeWrite: Array.isArray(effectiveDefinition.scopeWrite)
      ? effectiveDefinition.scopeWrite.filter((userId): userId is string => typeof userId === 'string')
      : undefined,

    // --- Stock (from definition) ---
    requiredStockIds: effectiveDefinition.requiredStockIds,
    stockRequirements: effectiveDefinition.stockRequirements,
    consumptionMode: effectiveDefinition.consumptionMode,

    // --- Recurring (renamed union) ---
    checklistType: legacyType,
    occurrences,
    recurringWindow: effectiveDefinition.recurringWindow,
    notRequiredFrom: effectiveDefinition.notRequiredFrom ?? null,
    repeatPaused: effectiveDefinition.repeatPaused,
    repeatPausedReason: effectiveDefinition.repeatPausedReason ?? null,
    isArchived: effectiveDefinition.isArchived,
    archivedAt: effectiveDefinition.archivedAt,
    archivedById: effectiveDefinition.archivedById,
    archivedByName: effectiveDefinition.archivedByName,
    preArchiveChecklistType: effectiveDefinition.preArchiveChecklistType ?? undefined,

    // --- Audit ---
    createdAt: effectiveDefinition.createdAt,
    createdById: effectiveDefinition.createdById,
    createdByName: effectiveDefinition.createdByName ?? undefined,
    updatedAt: effectiveDefinition.updatedAt,
    lastUpdatedBy: effectiveDefinition.lastUpdatedBy,
    lastUpdatedByName: effectiveDefinition.lastUpdatedByName,
    recordVersion: effectiveDefinition.recordVersion,
    statusSyncOrigin: completion?.statusSyncOrigin ?? null,
    statusSyncVersion: completion?.statusSyncVersion,
  }
}

// ---------------------------------------------------------------------------
// Bulk projection helper — takes a definitions list + per-type current-page +
// layout-version cache, emits a single ChecklistItem[] the way TimelineContext
// wants it.
// ---------------------------------------------------------------------------

export type BookCurrentPageMap = Record<
  ChecklistType,
  {
    page: ChecklistBookPage
    layoutVersion: ChecklistLayoutVersion | null
    detailVersion: ChecklistDetailVersion | null
  } | null
>

export const projectDefinitionsToChecklistItems = (
  definitions: readonly ChecklistDefinition[],
  perTypePages: BookCurrentPageMap,
): ChecklistItem[] => {
  const items: ChecklistItem[] = []
  for (const definition of definitions) {
    const bucket = perTypePages[definition.checklistType]
    const completion = bucket?.page.completions?.[definition.id]
    const layoutNode = bucket?.layoutVersion?.nodes?.[definition.id]
    items.push(
      projectDefinitionToChecklistItem(definition, {
        completion,
        layoutNode,
        detailVersion: bucket?.detailVersion ?? null,
        pageDetailPatch: bucket?.page.detailPatch?.[definition.id] ?? null,
        pageKey: bucket?.page.pageKey,
      }),
    )
  }
  return items
}

export const emptyBookCurrentPageMap = (): BookCurrentPageMap => ({
  INDIVIDUAL: null,
  DAILY: null,
  WEEKLY: null,
})
